import {
  CONFIRMATION_STATUSES,
  CONFIRMATION_LABELS,
  SHOWING,
  SHOWING_LABELS,
  INTENSITY,
  STRATEGIES,
  STRATEGY_LABELS,
  FIRE_STATUS,
  FIRE_STATUS_LABELS,
  THREAT_TAGS,
  ACTION_TAGS,
  SUPPORT_TAGS,
  labelForTag,
  toggleInArray,
  isConfirmedFire,
  INCIDENT_TYPES,
  INCIDENT_TYPE_LABELS,
  INCIDENT_TYPE_REGISTRY,
  activeIncidentTypes,
  computeTagRelevance,
  orderTags,
  type Incident,
  type IncidentType,
  type ConfirmationStatus,
  type Showing,
  type Intensity,
  type Strategy,
  type FireStatus,
  type ThreatTag,
  type ActionTag,
  type SupportTag,
} from '@duty/shared';
import { useState } from 'react';
import { createPortal } from 'react-dom';
import { Network } from 'lucide-react';
import { useIncidentStore } from '@/incident/store';
import { Field, Segmented, TagToggles, TextInput, type Option } from '@/components/ui/controls';
import { RelevanceGraphView } from '@/components/RelevanceGraphView';
import { VoiceCapturePanel } from '@/components/VoiceCapturePanel';
import { useVoiceSuggestions } from '@/hooks/useVoiceSuggestions';

const opt = <T extends string>(v: T, label: string): Option<T> => ({ value: v, label });
const tagOpts = <T extends string>(vals: readonly T[]): Option<T>[] =>
  vals.map((v) => opt(v, labelForTag(v)));

const confirmationOpts = CONFIRMATION_STATUSES.map((s) => opt(s, CONFIRMATION_LABELS[s]));
const showingOpts = SHOWING.map((s) => opt(s, SHOWING_LABELS[s]));
const intensityOpts = INTENSITY.map((s) => opt(s, s[0]!.toUpperCase() + s.slice(1)));
const strategyOpts = STRATEGIES.map((s) => opt(s, STRATEGY_LABELS[s]));
const fireStatusOpts = FIRE_STATUS.map((s) => opt(s, FIRE_STATUS_LABELS[s]));
const incidentTypeOpts = INCIDENT_TYPES.map((t) => opt(t, INCIDENT_TYPE_LABELS[t]));

/** Structured-first ITASC capture — chips/toggles/selects over the live incident. */
export function ItascCapture({ incident }: { incident: Incident }) {
  const { dispatch, insights, logEvent } = useIncidentStore();
  const { itasc } = incident;
  const confirmed = isConfirmedFire(incident.status);
  const [graphOpen, setGraphOpen] = useState(false);
  const voice = useVoiceSuggestions();

  // Order each chip picker in two layers: this incident type's static
  // priority tags first (fire vs. MVA vs. hazmat vocabularies differ), then
  // the live relevance graph reorders on top of that as the officer records
  // what's actually in front of them — e.g. flagging entrapment pulls rescue
  // and ambulance to the front; a life risk that reads clear with no
  // entrapment pushes them toward the back. Nothing is ever hidden — an
  // officer can always select any tag regardless of where it sits.
  // An incident can have more than one type simultaneously active (e.g. a
  // vehicle fire that's spread into an adjacent paddock is both VehicleFire
  // and GrassFire at once — see "Incident type" below) — union every active
  // type's priority list rather than just the primary one, so a type added
  // mid-incident surfaces its own vocabulary immediately.
  const activeTypes = activeIncidentTypes(incident);
  const profiles = activeTypes.map((t) => INCIDENT_TYPE_REGISTRY[t]);
  const unionPriority = (pick: (p: (typeof profiles)[number]) => string[]): string[] =>
    Array.from(new Set(profiles.flatMap(pick)));
  const relevance = computeTagRelevance(itasc);
  const threatOpts = tagOpts(
    orderTags(THREAT_TAGS, { priority: unionPriority((p) => p.priorityThreatTags), surface: relevance.surfaceThreats }),
  );
  const actionOpts = tagOpts(
    orderTags(ACTION_TAGS, {
      priority: unionPriority((p) => p.priorityActionTags),
      surface: relevance.surfaceActions,
      demote: relevance.demoteActions,
    }),
  );
  const supportOpts = tagOpts(
    orderTags(SUPPORT_TAGS, {
      priority: unionPriority((p) => p.prioritySupportTags),
      surface: relevance.surfaceSupport,
      demote: relevance.demoteSupport,
    }),
  );

  const primaryType = incident.typeConfirmed ?? incident.typeReported;
  const additionalTypeOpts = incidentTypeOpts.filter((o) => o.value !== primaryType);

  return (
    <div className="space-y-4">
      <VoiceCapturePanel voice={voice} />
      <div className="flex justify-end">
        <button
          type="button"
          onClick={() => setGraphOpen(true)}
          className="flex items-center gap-1.5 rounded-md border border-border bg-surface-2 px-2 py-1 text-[11px] text-muted hover:border-accent/50 hover:text-accent"
          title="Relevance graph — see how tags, capability and insights currently connect"
        >
          <Network size={12} /> Relevance graph
        </button>
      </div>
      {graphOpen &&
        createPortal(
          <RelevanceGraphView incident={incident} insights={insights} onClose={() => setGraphOpen(false)} />,
          document.body,
        )}
      {/* Incident type — what's initially thought to be one kind of incident
          can evolve or turn out to be another once fully assessed (a car fire
          spreading into an adjacent paddock, a "bush alight" call that's
          actually a hidden structure). Primary type is reclassifiable at any
          stage; additional types can be layered on top for the "genuinely
          both at once" case, without losing either type's rules/ITASC
          vocabulary/doctrine — see activeIncidentTypes(). */}
      <section className="space-y-2">
        <SectionLabel>Incident type</SectionLabel>
        <Field label="Primary type">
          <Segmented<IncidentType>
            options={incidentTypeOpts}
            value={primaryType}
            onChange={(v) => {
              if (!v || v === primaryType) return;
              dispatch({ type: 'set-primary-type', incidentType: v });
              logEvent('incident.type-changed', { from: primaryType, to: v, kind: 'primary' });
            }}
          />
        </Field>
        {incident.typeReported !== primaryType && (
          <p className="text-[11px] italic text-muted">
            Originally reported as {INCIDENT_TYPE_LABELS[incident.typeReported]}.
          </p>
        )}
        <Field label="Also active (e.g. spread into a second type)">
          <TagToggles<IncidentType>
            options={additionalTypeOpts}
            selected={incident.additionalTypes}
            onToggle={(v) => {
              const removing = incident.additionalTypes.includes(v);
              dispatch(removing ? { type: 'remove-incident-type', incidentType: v } : { type: 'add-incident-type', incidentType: v });
              logEvent('incident.type-changed', { type: v, kind: removing ? 'additional-removed' : 'additional-added' });
            }}
          />
        </Field>
      </section>

      {/* Confirmation / branching */}
      <section>
        <SectionLabel>Confirmation</SectionLabel>
        <Segmented<ConfirmationStatus>
          options={confirmationOpts}
          value={incident.status}
          onChange={(v) => v && dispatch({ type: 'set-confirmation', status: v })}
        />
        {!confirmed && incident.status !== 'unconfirmed' && (
          <p className="mt-1.5 text-[11px] italic text-muted">
            Not a working structure fire — confirmation checks paused; investigation / stand-down
            prompts apply.
          </p>
        )}
      </section>

      {/* I — Incident */}
      <section className="space-y-2.5">
        <SectionLabel>I — Incident</SectionLabel>
        <Field label="Showing">
          <Segmented<Showing>
            options={showingOpts}
            value={itasc.incident.showing}
            onChange={(v) => dispatch({ type: 'update-itasc', patch: { incident: { showing: v } } })}
            allowClear
            suggested={voice.showingSuggestion}
          />
        </Field>
        <Field label="Intensity">
          <Segmented<Intensity>
            options={intensityOpts}
            value={itasc.incident.intensity}
            onChange={(v) => dispatch({ type: 'update-itasc', patch: { incident: { intensity: v } } })}
            allowClear
          />
        </Field>
        {confirmed && (
          <Field label="Fire status">
            <Segmented<FireStatus>
              options={fireStatusOpts}
              value={itasc.incident.fireStatus}
              onChange={(v) => dispatch({ type: 'update-itasc', patch: { incident: { fireStatus: v } } })}
              allowClear
            />
          </Field>
        )}
        <Field label="Building type">
          <TextInput
            value={itasc.incident.buildingType ?? ''}
            onChange={(v) => dispatch({ type: 'update-itasc', patch: { incident: { buildingType: v } } })}
            placeholder="e.g. Single-storey dwelling"
            ariaLabel="Building type"
          />
        </Field>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={itasc.incident.locationConfirmed}
            onChange={(e) =>
              dispatch({ type: 'update-itasc', patch: { incident: { locationConfirmed: e.target.checked } } })
            }
          />
          Location confirmed
        </label>
      </section>

      {/* T — Threats */}
      <section className="space-y-2.5">
        <SectionLabel>T — Threats</SectionLabel>
        <Field label="Life risk">
          <TextInput
            value={itasc.threats.life ?? ''}
            onChange={(v) => dispatch({ type: 'update-itasc', patch: { threats: { life: v } } })}
            placeholder="e.g. Occupancy unknown"
            ariaLabel="Life risk"
          />
        </Field>
        <TagToggles<ThreatTag>
          options={threatOpts}
          selected={itasc.threats.selected}
          onToggle={(v) =>
            dispatch({ type: 'update-itasc', patch: { threats: { selected: toggleInArray(itasc.threats.selected, v) } } })
          }
          suggested={voice.threatQuotes}
        />
      </section>

      {/* A — Actions */}
      <section className="space-y-2.5">
        <SectionLabel>A — Actions</SectionLabel>
        <Field label="Strategy">
          <Segmented<Strategy>
            options={strategyOpts}
            value={itasc.actions.strategy}
            onChange={(v) => dispatch({ type: 'update-itasc', patch: { actions: { strategy: v } } })}
            allowClear
            suggested={voice.strategySuggestion}
          />
        </Field>
        <TagToggles<ActionTag>
          options={actionOpts}
          selected={itasc.actions.selected}
          onToggle={(v) =>
            dispatch({ type: 'update-itasc', patch: { actions: { selected: toggleInArray(itasc.actions.selected, v) } } })
          }
          suggested={voice.actionQuotes}
        />
      </section>

      {/* S — Support */}
      <section className="space-y-2.5">
        <SectionLabel>S — Support requested</SectionLabel>
        <TagToggles<SupportTag>
          options={supportOpts}
          selected={itasc.support.requested}
          onToggle={(v) =>
            dispatch({ type: 'update-itasc', patch: { support: { requested: toggleInArray(itasc.support.requested, v) } } })
          }
          suggested={voice.supportQuotes}
        />
      </section>

      {/* C — Command */}
      <section className="space-y-2.5">
        <SectionLabel>C — Command &amp; Control</SectionLabel>
        <div className="grid grid-cols-2 gap-2">
          <Field label="IC callsign">
            <TextInput
              value={itasc.command.incidentController ?? ''}
              onChange={(v) =>
                dispatch({ type: 'update-itasc', patch: { command: { incidentController: v, controlTaken: true } } })
              }
              placeholder="e.g. Bungendore 1"
              ariaLabel="Incident controller"
            />
          </Field>
          <Field label="Radio channel">
            <TextInput
              value={itasc.command.radioChannel ?? ''}
              onChange={(v) => dispatch({ type: 'update-itasc', patch: { command: { radioChannel: v } } })}
              placeholder="e.g. Channel X"
              ariaLabel="Radio channel"
            />
          </Field>
        </div>
        <Field label="Staging">
          <TextInput
            value={itasc.command.staging ?? ''}
            onChange={(v) => dispatch({ type: 'update-itasc', patch: { command: { staging: v } } })}
            placeholder="e.g. School car park"
            ariaLabel="Staging"
          />
        </Field>
      </section>
    </div>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <h3 className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-accent">{children}</h3>
  );
}
